import { useState } from 'react';
import {
  Headphones,
  Loader2,
  X,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { SupportRequest } from '@/hooks/useRemoteSession';

interface RequestSupportButtonProps {
  hasPendingRequest: boolean;
  pendingRequest: SupportRequest | null;
  onCreateRequest: (data: {
    message?: string;
    category?: 'technical' | 'transaction' | 'customer' | 'hardware' | 'other';
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    registerId?: string;
  }) => void;
  onCancelRequest: (requestId: string) => void;
  isCreating?: boolean;
  isCancelling?: boolean;
  registerId?: string;
  variant?: 'default' | 'compact';
  className?: string;
}

/**
 * Button for cashiers to request support from administrators.
 * Shows a form to describe the issue and tracks pending requests.
 */
export function RequestSupportButton({
  hasPendingRequest,
  pendingRequest,
  onCreateRequest,
  onCancelRequest,
  isCreating = false,
  isCancelling = false,
  registerId,
  variant = 'default',
  className,
}: RequestSupportButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState<'technical' | 'transaction' | 'customer' | 'hardware' | 'other'>('other');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal');

  const handleSubmit = () => {
    onCreateRequest({
      message: message.trim() || undefined,
      category,
      priority,
      registerId,
    });
    setMessage('');
    setCategory('other');
    setPriority('normal');
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    if (pendingRequest) {
      onCancelRequest(pendingRequest.id);
    }
  };

  // If there's a pending request, show the status
  if (hasPendingRequest && pendingRequest) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size={variant === 'compact' ? 'sm' : 'default'}
            className={cn(
              'gap-2 border-amber-500/50 text-amber-600 hover:bg-amber-500/10 dark:text-amber-400',
              className
            )}
          >
            <Clock className="h-4 w-4 animate-pulse" />
            <span className={variant === 'compact' ? 'hidden sm:inline' : ''}>
              Support Requested
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h4 className="font-medium">Support Request Pending</h4>
                <p className="text-sm text-muted-foreground">
                  Waiting for an administrator to connect
                </p>
              </div>
              <Badge variant="secondary" className="bg-amber-500/10 text-amber-600 shrink-0">
                Pending
              </Badge>
            </div>

            {pendingRequest.message && (
              <div className="p-2 bg-muted rounded-md text-sm">
                <p className="text-muted-foreground text-xs mb-1">Your message:</p>
                <p>{pendingRequest.message}</p>
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>
                Requested {new Date(pendingRequest.created_at).toLocaleTimeString()}
              </span>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2"
              onClick={handleCancel}
              disabled={isCancelling}
            >
              {isCancelling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              Cancel Request
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size={variant === 'compact' ? 'sm' : 'default'}
        className={cn('gap-2', className)}
        onClick={() => setIsModalOpen(true)}
      >
        <Headphones className="h-4 w-4" />
        <span className={variant === 'compact' ? 'hidden sm:inline' : ''}>
          Request Support
        </span>
      </Button>

      <ResponsiveModal open={isModalOpen} onOpenChange={setIsModalOpen}>
        <ResponsiveModalContent maxWidth="sm">
          <ResponsiveModalHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Headphones className="h-5 w-5 text-primary" />
              </div>
              <div>
                <ResponsiveModalTitle>Request Support</ResponsiveModalTitle>
                <ResponsiveModalDescription>
                  An administrator will connect to help you
                </ResponsiveModalDescription>
              </div>
            </div>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Issue Category</Label>
                <Select value={category} onValueChange={(v) => setCategory(v as any)}>
                  <SelectTrigger id="category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technical">Technical Issue</SelectItem>
                    <SelectItem value="transaction">Transaction Help</SelectItem>
                    <SelectItem value="customer">Customer Issue</SelectItem>
                    <SelectItem value="hardware">Hardware Problem</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Priority */}
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={priority} onValueChange={(v) => setPriority(v as any)}>
                  <SelectTrigger id="priority">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low - Can wait</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High - Need help soon</SelectItem>
                    <SelectItem value="urgent">Urgent - Customer waiting</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message">
                  Describe the Issue <span className="text-muted-foreground">(optional)</span>
                </Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="What do you need help with?"
                  rows={3}
                />
              </div>

              {/* Info */}
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <div className="flex items-start gap-2 text-sm text-blue-700 dark:text-blue-300">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>
                    When an administrator connects, they will be able to see and control your screen
                    to help resolve the issue.
                  </p>
                </div>
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="blaze"
              onClick={handleSubmit}
              disabled={isCreating}
            >
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Headphones className="h-4 w-4 mr-2" />
              )}
              Request Support
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </>
  );
}

/**
 * Compact version that can be added to the POS header.
 */
export function RequestSupportCompact(props: Omit<RequestSupportButtonProps, 'variant'>) {
  return <RequestSupportButton {...props} variant="compact" />;
}

